<script lang="ts">
    import {onMount, tick} from 'svelte';
    import Chart, { type Point } from 'chart.js/auto';
    import zoomPlugin from 'chartjs-plugin-zoom';
    import { formatDuration } from '../../utils/style';
    import type { IRouteInfo } from '../../utils/types';
    import type {RouteMeasurements} from "../../models/route-measurements.model";
    import {
        MEASUREMENT_DURATION_FIELDS, MEASUREMENT_FIELD_DELAY_DURATION,
        MEASUREMENT_FIELD_DELAY_FACTOR,
        MEASUREMENT_FIELD_ROUTE_ID,
        MEASUREMENT_FIELD_ROUTE_NAME
    } from "../../constants/measurement-table-constants";
    import type {ChartData} from "../../models/chart-data.model";
    import {routeInfoStore} from "../../stores/route-info.store";
    const dateFormatter = new Intl.DateTimeFormat('nl-NL', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
    
    export let activeMeasurementProperty: string = MEASUREMENT_FIELD_DELAY_DURATION;
    export let handleZoomChange: () => void;
    export let chart: Chart;
    export let measurementsByRouteIdSortedByTimestampAsc: { [routeId: string]: RouteMeasurements[] };
    export let measurementTimeStamps: Date[]
    export let showRouteFilter: boolean = true;

    $: updateChartImpl(
        measurementsByRouteIdSortedByTimestampAsc,
        measurementTimeStamps,
        activeMeasurementProperty,
        $routeInfoStore
    );

    let chartData: ChartData;
    let chartContainer: HTMLCanvasElement;

    onMount(async () => {
        await tick();
        chartData = {
            labels: [],
            datasets: []
        };
        chart = instantiateChart();
        updateChartImpl(measurementsByRouteIdSortedByTimestampAsc, measurementTimeStamps, activeMeasurementProperty, $routeInfoStore);
    });

    function instantiateChart(): Chart {
        return new Chart(
            chartContainer,
            {
                type: 'line',
                data: chartData,
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: false,
                    scales: {
                        y: {
                            suggestedMin: 0,
                            suggestedMax: 0,
                            ticks: {
                                callback: yTickCallback
                            }
                        },
                        x: {
                            afterDataLimits: handleZoomChange
                        }
                    },
                    interaction: {
                        intersect: false,
                        mode: 'nearest',
                        axis: 'x'
                    },
                    elements: {
                        point: {
                            radius: 0
                        }
                    },
                    plugins: {
                        zoom: {
                            pan: {
                                enabled: true,
                                mode: 'x'
                            },
                            zoom: {
                                wheel: {
                                    enabled: true
                                },
                                pinch: {
                                    enabled: true
                                },
                                mode: 'x'
                            }
                        },
                        legend: {
                            display: showRouteFilter,
                            labels: {
                                boxWidth: 16,
                                boxHeight: 16,
                                padding: 30
                            }
                        },
                        tooltip: {
                            callbacks: {
                                title: (contexts) => {
                                    const x = contexts[0]?.parsed?.x;
                                    return x !== undefined ? chartData.labels[x] : '';
                                },
                                label: tooltipLabelCallback
                            }
                        }
                    }
                },
                plugins: [zoomPlugin]
            }
        );
    }

    function yTickCallback(value) {
        if (MEASUREMENT_DURATION_FIELDS.includes(activeMeasurementProperty)) {
            return formatDuration(value as number);
        } else {
            return value;
        }
    }

    function tooltipLabelCallback(context) {
        const label = context.dataset.label || '';

        if (context.parsed.y !== null) {
            const value = formatLabel(context.parsed.y);
            return `${label}: ${value}`;
        } else {
            return label;
        }
    }

    function formatLabel(value: number) {
        if(MEASUREMENT_DURATION_FIELDS.includes(activeMeasurementProperty))
            return formatDuration(value);

        return `${value.toFixed(0)} ${activeMeasurementProperty === MEASUREMENT_FIELD_DELAY_FACTOR ? '(%)' : '(km/u)'}`;
    }

    // some Claude powered stuff that I don't really understand but does improve performance
    function lttbDecimate(data: Point[], threshold: number): Point[] {
        const n = data.length;
        if (n <= threshold) return data;

        const sampled: Point[] = new Array(threshold);
        const bucketSize = (n - 2) / (threshold - 2);
        let a = 0;
        sampled[0] = data[0];

        for (let i = 0; i < threshold - 2; i++) {
            const nextFrom = Math.floor((i + 1) * bucketSize) + 1;
            const nextTo = Math.min(Math.floor((i + 2) * bucketSize) + 1, n);
            let avgX = 0, avgY = 0;
            for (let j = nextFrom; j < nextTo; j++) { avgX += (data[j].x as number); avgY += (data[j].y as number); }
            const nextLen = nextTo - nextFrom;
            avgX /= nextLen; avgY /= nextLen;

            const curFrom = Math.floor(i * bucketSize) + 1;
            const curTo = Math.floor((i + 1) * bucketSize) + 1;
            const pA = data[a];
            let maxArea = -1, nextA = curFrom;
            for (let j = curFrom; j < curTo; j++) {
                const area = Math.abs(
                    ((pA.x as number) - avgX) * ((data[j].y as number) - (pA.y as number)) -
                    ((pA.x as number) - (data[j].x as number)) * (avgY - (pA.y as number))
                ) * 0.5;
                if (area > maxArea) { maxArea = area; nextA = j; }
            }
            sampled[i + 1] = data[nextA];
            a = nextA;
        }

        sampled[threshold - 1] = data[n - 1];
        return sampled;
    }

    let lastLabelTimestamps: Date[] | undefined;
    let timestampIndexByTime: Map<number, number> | undefined;
    let lastTimestamps: Date[] | undefined;

    function updateChartImpl(
        measurementsByRouteIdSortedByTimestampAsc: { [routeId: string]: RouteMeasurements[] },
        measurementTimeStamps: Date[],
        activeMeasurementProperty: string,
        routeInfo: { [id: string]: IRouteInfo }
    ) {
        if (!chart) return;
        if (!chartData) return;
        if (!measurementsByRouteIdSortedByTimestampAsc) return;
        if (!measurementTimeStamps) return;

        if (lastLabelTimestamps !== measurementTimeStamps) {
            chartData.labels = measurementTimeStamps.map((timestamp) => dateFormatter.format(timestamp));
            lastLabelTimestamps = measurementTimeStamps;
        }
        if (measurementTimeStamps !== lastTimestamps) {
            timestampIndexByTime = new Map(
                measurementTimeStamps.map((timestamp, index) => [timestamp.getTime(), index])
            );
            lastTimestamps = measurementTimeStamps;
        }

        chartData.datasets = Object.values(measurementsByRouteIdSortedByTimestampAsc)
            .filter((routeMeasurementsByTimestampAsc) => routeMeasurementsByTimestampAsc.length > 0)
            .map((routeMeasurementsByTimestampAsc) => {
                const firstRouteMeasurement = routeMeasurementsByTimestampAsc[0];
                const routeId = firstRouteMeasurement[MEASUREMENT_FIELD_ROUTE_ID] ?? '';
                const currentRouteInfo = routeInfo[routeId];
                const currentRouteName = firstRouteMeasurement[MEASUREMENT_FIELD_ROUTE_NAME];
                const colour = currentRouteInfo?.color ?? [0, 0, 0];

                const values: Point[] = routeMeasurementsByTimestampAsc
                    .map((measurement) => {
                        const timestampIndex = timestampIndexByTime.get(measurement.timestamp.getTime());

                        if (timestampIndex === undefined) {
                            return undefined;
                        }

                        return {
                            x: timestampIndex,
                            y: measurement[activeMeasurementProperty]
                        };
                    })
                    .filter((point): point is Point => point !== undefined);

                const decimated = lttbDecimate(values, 500);

                return {
                    label: currentRouteName,
                    data: decimated,
                    parsing: false,
                    borderColor: `rgb(${colour[0]}, ${colour[1]}, ${colour[2]})`,
                    fill: false,
                    tension: false,
                    hidden: currentRouteInfo?.hidden === true
                };
            });

        chart.update('none');

        const len = measurementTimeStamps.length ?? 0;

        if (chart.scales?.x && len > 20) {
            chart.pan({ x: -150 }, undefined, 'none');
        }
    }
</script>

<div style="width: 100%; height: 80%; position: relative;">
    <canvas bind:this={chartContainer} />
</div>
